import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { AdminGuard, AdminNav } from "../components/admin/AdminShell";
import { formatPrice } from "../lib/format";
import {
  ArrowLeft, Truck, Package, MapPin, ShieldCheck, DownloadSimple,
  Certificate, Warning, Cube, Info, EnvelopeSimple, ArrowClockwise,
  CheckCircle, XCircle, Paperclip, ShieldWarning, MagnifyingGlass, Copy,
} from "@phosphor-icons/react";

function Row({ label, value, mono = false, testid }) {
  return (
    <div className="flex justify-between items-baseline text-sm">
      <span className="text-brand-muted">{label}</span>
      <span
        className={mono ? "font-mono text-brand-text" : "font-semibold text-brand-navy"}
        data-testid={testid}
      >
        {value ?? "—"}
      </span>
    </div>
  );
}

export default function AdminOrderDetail() {
  const { order_id } = useParams();
  const [order, setOrder] = useState(null);
  const [rates, setRates] = useState(null);
  const [rateBusy, setRateBusy] = useState(false);
  const [labelBusy, setLabelBusy] = useState(false);
  const [retryBusy, setRetryBusy] = useState({});
  const [dryRunPlan, setDryRunPlan] = useState(null);
  const [dryRunBusy, setDryRunBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/orders/${order_id}`);
      setOrder(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Order not found");
    }
  }, [order_id]);

  useEffect(() => { load(); }, [load]);

  const previewReconcile = async () => {
    if (!order?.session_id) {
      toast.error("Order has no Stripe session id — cannot preview reconcile.");
      return;
    }
    setDryRunBusy(true);
    setDryRunPlan(null);
    try {
      const { data } = await api.post(
        `/admin/orders/reconcile/${order.session_id}`,
        null,
        { params: { dry_run: true } }
      );
      setDryRunPlan(data);
      toast.success("Dry-run preview loaded. No mutations were made.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Dry-run preview failed");
    } finally {
      setDryRunBusy(false);
    }
  };

  const fetchRates = async () => {
    setRateBusy(true);
    try {
      const { data } = await api.get(`/fulfillment/orders/${order_id}/shippo/rates`);
      setRates(data);
      const preview = data?.shipment?.dry_run
        ? "Shippo dry-run — no live token configured"
        : `${(data?.shipment?.rates || []).length} live rate(s) fetched`;
      toast.success(preview);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to fetch rates");
    } finally {
      setRateBusy(false);
    }
  };

  const validateAddress = async () => {
    try {
      const { data } = await api.post(`/fulfillment/orders/${order_id}/shippo/validate-address`);
      if (data?.dry_run) {
        toast.info("Shippo address validation is in dry-run (no live token).");
      } else if (data?.validation_results?.is_valid) {
        toast.success("Shippo confirmed the shipping address is deliverable.");
      } else {
        toast.warning("Shippo could not verify the address. Review and correct.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Address validation failed");
    }
  };

  const purchaseLabel = async (rate_id) => {
    if (!rate_id) {
      toast.error("Select a rate before purchasing a label.");
      return;
    }
    if (!window.confirm("Purchase a Shippo shipping label with the selected rate? This will attempt to buy postage (preview mode is safe).")) {
      return;
    }
    setLabelBusy(true);
    try {
      const { data } = await api.post(
        `/fulfillment/orders/${order_id}/shippo/purchase-label`,
        { rate_id, confirm: true },
      );
      toast.success(
        data?.shippo?.dry_run
          ? "Dry-run label recorded (no live postage burnt)."
          : "Label purchased — tracking number saved on the order."
      );
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Label purchase failed");
    } finally {
      setLabelBusy(false);
    }
  };

  const retryNotification = async (notification_type) => {
    setRetryBusy((s) => ({ ...s, [notification_type]: true }));
    try {
      await api.post(`/admin/orders/${order_id}/fulfillment-emails/${notification_type}/retry`);
      toast.success(`Retry queued for ${notification_type.replace("_", " ")}.`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Retry failed");
    } finally {
      setRetryBusy((s) => ({ ...s, [notification_type]: false }));
    }
  };

  if (!order) {
    return (
      <AdminGuard>
        <AdminNav />
        <div className="max-w-5xl mx-auto px-4 py-12 text-brand-muted">Loading order…</div>
      </AdminGuard>
    );
  }

  const shippo = order.shippo || {};
  const items = order.items || [];
  const totalVials = items.reduce((n, it) => n + (it.quantity || 0), 0);
  const addr = order.shipping_address || {};
  const shippoRates = rates?.shipment?.rates || [];
  const dryRun = Boolean(rates?.shipment?.dry_run);

  return (
    <AdminGuard>
      <AdminNav />
      <div className="max-w-6xl mx-auto px-4 py-10" data-testid="admin-order-detail-page">
        <Link
          to="/admin/orders"
          data-testid="admin-order-back"
          className="inline-flex items-center gap-2 text-sm text-brand-muted hover:text-brand-navy mb-6"
        >
          <ArrowLeft size={14} weight="regular" /> Back to orders
        </Link>

        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="overline mb-2">Order</div>
            <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-brand-navy">
              {order.customer_email || "Guest order"}
            </h1>
            <div className="mt-2 font-mono text-xs text-brand-muted">
              {order.id} · session {order.session_id}
            </div>
          </div>
          <div className="text-right">
            <div className="font-display font-bold text-3xl text-brand-navy">
              {formatPrice(order.total_cents || 0)}
            </div>
            <div className="text-[11px] text-brand-muted font-mono uppercase tracking-widest mt-1">
              {order.status}
            </div>
          </div>
        </div>

        <FulfillmentInvariantAlerts
          order={order}
          onPreviewReconcile={previewReconcile}
          dryRunBusy={dryRunBusy}
          dryRunPlan={dryRunPlan}
        />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left column — items + address */}
          <div className="lg:col-span-8 space-y-6">
            <div className="mps-card p-6 lg:p-8" data-testid="order-items-card">
              <div className="overline mb-4 flex items-center gap-2">
                <Cube size={12} weight="duotone" className="text-brand-accent" /> Items · {totalVials} vials
              </div>
              <div className="divide-y divide-brand-border">
                {items.map((it, idx) => (
                  <div key={idx} className="py-3 flex items-start justify-between gap-3">
                    <div>
                      <div className="font-semibold text-brand-navy">
                        {it.public_display_name || it.public_sku}
                      </div>
                      <div className="text-xs text-brand-muted font-mono mt-0.5">
                        {it.public_sku}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-semibold text-brand-navy">×{it.quantity}</div>
                      <div className="text-xs text-brand-muted">
                        {formatPrice((it.unit_amount_cents || 0) * (it.quantity || 1))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="border-t border-brand-border mt-4 pt-4 space-y-2">
                <Row label="Subtotal" value={formatPrice(order.subtotal_cents || 0)} testid="order-subtotal" />
                <Row label="Shipping" value={formatPrice(order.shipping_cents || 0)} testid="order-shipping" />
                <Row label="Tax" value={formatPrice(order.tax_cents || 0)} testid="order-tax" />
                <Row label="Total" value={formatPrice(order.total_cents || 0)} testid="order-total" />
              </div>
            </div>

            <div className="mps-card p-6 lg:p-8" data-testid="order-shipping-card">
              <div className="overline mb-4 flex items-center gap-2">
                <MapPin size={12} weight="duotone" className="text-brand-accent" /> Shipping address
              </div>
              <div className="text-sm text-brand-text leading-relaxed">
                <div className="font-semibold text-brand-navy">{addr.name || "—"}</div>
                <div>{addr.line1}{addr.line2 ? ` · ${addr.line2}` : ""}</div>
                <div>{addr.city}, {addr.state} {addr.postal_code}</div>
                <div>{addr.country || "US"}</div>
              </div>
              <button
                onClick={validateAddress}
                data-testid="shippo-validate-btn"
                className="mt-4 inline-flex items-center gap-2 h-9 px-3 rounded-[10px] border border-brand-border text-brand-navy text-xs font-semibold hover:bg-brand-surface transition-colors"
              >
                <ShieldCheck size={12} weight="regular" /> Validate with Shippo
              </button>
            </div>
          </div>

          {/* Right column — Shippo fulfillment */}
          <div className="lg:col-span-4 space-y-6">
            <div className="mps-card p-6 lg:p-7 sticky top-32" data-testid="order-fulfillment-card">
              <div className="overline mb-4 flex items-center gap-2">
                <Truck size={12} weight="duotone" className="text-brand-accent" /> Shippo fulfillment
              </div>

              {shippo.tracking_number ? (
                <div className="space-y-2.5 text-sm">
                  <Row label="Carrier" value={shippo.carrier} testid="fulfillment-carrier" />
                  <Row label="Service" value={shippo.service} testid="fulfillment-service" />
                  <Row label="Tracking" value={shippo.tracking_number} mono testid="fulfillment-tracking" />
                  <Row label="Status" value={shippo.tracking_status || "PRE_TRANSIT"} testid="fulfillment-status" />
                  {shippo.dry_run && (
                    <div className="mt-3 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-[10px] p-2.5 flex gap-2">
                      <Info size={14} weight="regular" className="shrink-0 mt-0.5" />
                      Recorded in preview dry-run mode — no live label was purchased.
                    </div>
                  )}
                  {shippo.label_url && (
                    <a
                      href={shippo.label_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="fulfillment-label-link"
                      className="mt-4 inline-flex items-center gap-2 h-10 px-4 rounded-[12px] border border-brand-navy bg-brand-navy text-white text-xs font-semibold hover:bg-brand-navy2 transition-colors"
                    >
                      <DownloadSimple size={14} weight="bold" /> Download Label PDF
                    </a>
                  )}
                  {shippo.tracking_url && (
                    <a
                      href={shippo.tracking_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="fulfillment-tracking-link"
                      className="mt-2 block text-center text-xs text-brand-accent hover:underline"
                    >
                      Track shipment ↗
                    </a>
                  )}
                </div>
              ) : (
                <div>
                  <p className="text-sm text-brand-muted leading-relaxed">
                    Fetch a live rate quote from Shippo. Shipments are packed in the
                    MPS 96-Vial Carton (10 × 7 × 2.5 in) with signature required.
                  </p>
                  <button
                    onClick={fetchRates}
                    disabled={rateBusy}
                    data-testid="shippo-fetch-rates-btn"
                    className="mt-4 w-full inline-flex items-center justify-center gap-2 h-11 px-4 rounded-[12px] bg-brand-accent hover:bg-[#1E6BD8] text-white font-semibold text-sm disabled:opacity-50 transition-colors"
                  >
                    {rateBusy ? "Fetching rates…" : "Fetch Shippo rates"}
                  </button>

                  {rates && (
                    <div className="mt-5" data-testid="shippo-rates-panel">
                      <div className="overline mb-2 flex items-center gap-2">
                        <Package size={12} weight="duotone" className="text-brand-accent" />
                        Rates {dryRun && <span className="text-amber-700 font-mono">(dry-run)</span>}
                      </div>
                      <div className="text-[11px] text-brand-muted font-mono mb-2">
                        {rates.carton} · {rates.vial_count} vials · {rates.parcel?.weight} oz
                      </div>
                      {shippoRates.length === 0 ? (
                        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-[10px] p-3 flex gap-2">
                          <Warning size={14} weight="regular" className="shrink-0 mt-0.5" />
                          {dryRun
                            ? "Shippo is in preview dry-run — configure SHIPPO_API_TOKEN to fetch live rates."
                            : "Shippo returned no rates. Verify the destination address."}
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {shippoRates.map((r) => (
                            <div
                              key={r.object_id || `${r.provider}-${r.servicelevel?.token}`}
                              data-testid={`shippo-rate-${r.provider}-${r.servicelevel?.token}`}
                              className="flex items-center justify-between border border-brand-border rounded-[12px] p-3 hover:border-brand-navy transition-colors"
                            >
                              <div>
                                <div className="text-sm font-semibold text-brand-navy">
                                  {r.provider} · {r.servicelevel?.name || r.servicelevel?.token}
                                </div>
                                <div className="text-[11px] text-brand-muted">
                                  Est. {r.estimated_days || "—"} days
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="font-semibold text-brand-navy">
                                  {r.amount ? `$${Number(r.amount).toFixed(2)}` : "—"}
                                </div>
                                <button
                                  onClick={() => purchaseLabel(r.object_id)}
                                  disabled={labelBusy || !r.object_id}
                                  data-testid={`shippo-buy-${r.object_id}`}
                                  className="mt-1 text-[10px] font-semibold uppercase tracking-widest text-brand-accent hover:text-brand-navy disabled:opacity-40"
                                >
                                  {labelBusy ? "Buying…" : "Buy label"}
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="mt-6 pt-5 border-t border-brand-border text-[11px] text-brand-muted space-y-1.5">
                <div className="flex items-center gap-1.5">
                  <Certificate size={11} weight="regular" /> MPS 96-Vial Carton · 10 × 7 × 2.5 in
                </div>
                <div>Room-temperature lyophilized powder — signature required on delivery.</div>
              </div>
            </div>

            <NotificationsPanel
              order={order}
              retryBusy={retryBusy}
              onRetry={retryNotification}
            />
          </div>
        </div>
      </div>
    </AdminGuard>
  );
}


// ─── Notifications panel (Phase 28o) ─────────────────────────────────────

const N_LABELS = {
  customer_shipped: "Customer — shipped confirmation",
  seller_label:     "Fulfillment — label + packing slip",
};

const STATUS_STYLES = {
  sent:    { icon: CheckCircle, cls: "text-emerald-600 bg-emerald-50 border-emerald-200", label: "Sent" },
  pending: { icon: Info,        cls: "text-amber-700 bg-amber-50 border-amber-200",       label: "Pending" },
  failed:  { icon: XCircle,     cls: "text-red-700 bg-red-50 border-red-200",             label: "Failed" },
};

function NotificationsPanel({ order, retryBusy, onRetry }) {
  const notifs = order?.fulfillment_notifications || {};
  const emailStatus = order?.fulfillment_email_status || {};
  const hasAny =
    notifs.customer_shipped || notifs.seller_label ||
    emailStatus.customer || emailStatus.seller;

  return (
    <div className="mps-card p-6 lg:p-7" data-testid="order-notifications-card">
      <div className="overline mb-4 flex items-center gap-2">
        <EnvelopeSimple size={12} weight="duotone" className="text-brand-accent" />
        Fulfillment notifications
      </div>

      {!hasAny ? (
        <div className="text-sm text-brand-muted leading-relaxed">
          Fulfillment emails fire automatically the moment a Shippo label is
          purchased. Once that happens you’ll see the customer and seller
          delivery statuses here.
        </div>
      ) : (
        <div className="space-y-4">
          {["customer_shipped", "seller_label"].map((t) => (
            <NotificationRow
              key={t}
              type={t}
              row={notifs[t]}
              legacySummary={t === "customer_shipped" ? emailStatus.customer : emailStatus.seller}
              busy={!!retryBusy[t]}
              onRetry={() => onRetry(t)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function NotificationRow({ type, row, legacySummary, busy, onRetry }) {
  // row may be null when the webhook has not yet fired. In that case fall
  // back to the coarser `fulfillment_email_status` summary so admins still
  // see *something*.
  const status = row?.status || legacySummary || "—";
  const style = STATUS_STYLES[status] || {
    icon: Info,
    cls: "text-brand-muted bg-brand-surface border-brand-border",
    label: status,
  };
  const Icon = style.icon;
  const flagged = row?.flag_for_admin_review;
  const attach = row?.label_attachment_status;

  return (
    <div
      data-testid={`notification-row-${type}`}
      className="border border-brand-border rounded-[12px] p-4 bg-white"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-brand-navy">
            {N_LABELS[type]}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px]">
            <span
              data-testid={`notification-status-${type}`}
              className={`inline-flex items-center gap-1 px-2 py-0.5 border font-mono uppercase tracking-wider ${style.cls}`}
            >
              <Icon size={11} weight="bold" /> {style.label}
            </span>
            {typeof row?.attempts === "number" && (
              <span className="text-brand-muted font-mono">
                {row.attempts} attempt{row.attempts === 1 ? "" : "s"}
              </span>
            )}
            {row?.sent_at && (
              <span className="text-brand-muted font-mono">
                sent {new Date(row.sent_at).toLocaleString()}
              </span>
            )}
            {attach && type === "seller_label" && (
              <span
                data-testid="notification-attachment-status"
                className={`inline-flex items-center gap-1 px-2 py-0.5 border font-mono uppercase tracking-wider ${
                  attach === "attached"
                    ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                    : "text-amber-700 bg-amber-50 border-amber-200"
                }`}
              >
                <Paperclip size={11} weight="bold" />
                {attach === "attached" ? "PDF attached" : "Link fallback"}
              </span>
            )}
            {flagged && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 border font-mono uppercase tracking-wider text-red-700 bg-red-50 border-red-200">
                <Warning size={11} weight="bold" /> Needs review
              </span>
            )}
          </div>
          {row?.flag_reason && (
            <div className="mt-2 text-[11px] text-brand-muted">
              Reason: <span className="font-mono">{row.flag_reason}</span>
            </div>
          )}
          {row?.provider_error && (
            <div className="mt-2 text-[11px] text-red-700 break-words">
              Provider error: <span className="font-mono">{row.provider_error}</span>
            </div>
          )}
          {row?.provider_message_id && (
            <div className="mt-1 text-[11px] text-brand-muted">
              Provider msg id: <span className="font-mono">{row.provider_message_id}</span>
            </div>
          )}
        </div>
        <button
          onClick={onRetry}
          disabled={busy}
          data-testid={`notification-retry-${type}`}
          className="shrink-0 inline-flex items-center gap-2 h-9 px-3 rounded-[10px] border border-brand-navy text-brand-navy text-xs font-semibold hover:bg-brand-navy hover:text-white disabled:opacity-40 transition-colors"
        >
          <ArrowClockwise size={13} weight="bold" className={busy ? "animate-spin" : ""} />
          {busy ? "Resending…" : "Resend"}
        </button>
      </div>
    </div>
  );
}


// ─── Fulfillment invariant alerts (duplicate / missing address / dry-run) ─
//
// Renders a highly visible warning block whenever the order shows any
// invariant violation (duplicate_conflict, is_duplicate_tombstone,
// missing_address, needs_action) and exposes the SAFE dry-run reconcile
// preview. The preview button ONLY calls the ?dry_run=true endpoint —
// there is no wet-run trigger in this UI (by design).

const REQUIRED_ADDRESS_FIELDS = ["line1", "city", "state", "postal_code", "country"];

function computeMissingAddress(order) {
  const addr = order?.shipping_address || {};
  return REQUIRED_ADDRESS_FIELDS.filter((f) => !(addr[f] || "").toString().trim());
}

function FulfillmentInvariantAlerts({ order, onPreviewReconcile, dryRunBusy, dryRunPlan }) {
  if (!order) return null;
  const missing = computeMissingAddress(order);
  // Legacy duplicate rows (created before the Phase-28q tombstone fields
  // were introduced) do NOT carry duplicate_conflict_role or
  // is_duplicate_tombstone, but the server-side GET /admin/orders/{id}
  // now returns a live `duplicate_check` computed from Mongo (no Stripe
  // call, no mutation). Trust either signal.
  const liveDupCheck = order.duplicate_check;
  const liveDupDetected = Boolean(liveDupCheck?.is_duplicate);
  const isDup = order.finalization_last_step === "duplicate_conflict" ||
                order.is_duplicate_tombstone ||
                order.duplicate_conflict_role ||
                liveDupDetected;
  const isTomb  = Boolean(order.is_duplicate_tombstone);
  const needsAction = order.finalization_status === "needs_action";
  const anyAlert = isDup || missing.length > 0 || needsAction;

  if (!anyAlert) return null;

  // Prefer explicit role fields; fall back to the live check for legacy rows.
  const displayRole = order.duplicate_conflict_role
    || (isTomb ? "duplicate (tombstone)" : null)
    || (liveDupDetected
        ? (liveDupCheck.this_order_is_canonical ? "canonical (legacy — detected live)" : "duplicate (legacy — detected live)")
        : "canonical");
  const displayCanonicalLink = order.duplicate_of
    || (liveDupDetected && !liveDupCheck.this_order_is_canonical
        ? liveDupCheck.canonical_order_id : null);
  const liveDupIds = liveDupDetected
    ? (liveDupCheck.this_order_is_canonical
        ? (liveDupCheck.duplicate_order_ids || [])
        : [liveDupCheck.canonical_order_id].filter(Boolean))
    : [];

  return (
    <div className="space-y-3 mb-8" data-testid="invariant-alerts">
      {isDup && (
        <div
          data-testid="invariant-alert-duplicate"
          className="rounded-[14px] border border-red-300 bg-red-50 p-5"
        >
          <div className="flex items-start gap-3">
            <ShieldWarning size={22} weight="duotone" className="text-red-700 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="text-red-900 font-display font-semibold text-base">
                Duplicate-order conflict — automated fulfillment halted
              </div>
              <div className="mt-2 text-sm text-red-900 leading-relaxed">
                More than one order row exists for this Stripe payment.
                To protect the merchant, this order will not decrement
                inventory again, buy a second Shippo label, or send a
                second customer/admin email.
              </div>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-[11px]">
                <div className="bg-white border border-red-200 rounded-[10px] px-3 py-2">
                  <div className="text-red-700 font-mono uppercase tracking-widest">Role</div>
                  <div className="font-semibold text-red-900" data-testid="invariant-duplicate-role">
                    {displayRole}
                  </div>
                </div>
                {displayCanonicalLink && (
                  <div className="bg-white border border-red-200 rounded-[10px] px-3 py-2">
                    <div className="text-red-700 font-mono uppercase tracking-widest">
                      {liveDupCheck?.this_order_is_canonical ? "Duplicate order" : "Canonical order"}
                    </div>
                    <Link
                      to={`/admin/orders/${displayCanonicalLink}`}
                      className="font-mono text-red-900 underline break-all"
                      data-testid="invariant-duplicate-canonical-link"
                    >
                      {displayCanonicalLink}
                    </Link>
                  </div>
                )}
              </div>
              {liveDupDetected && liveDupIds.length > 1 && (
                <div
                  data-testid="invariant-duplicate-live-list"
                  className="mt-3 text-[11px] text-red-900 font-mono"
                >
                  All rows in this conflict: {liveDupIds.join(", ")}
                </div>
              )}
              {liveDupDetected && !order.duplicate_conflict_role && (
                <div
                  data-testid="invariant-duplicate-legacy-notice"
                  className="mt-2 text-[11px] text-red-800"
                >
                  Legacy row — detected live from session_id / payment_intent_id.
                  Tombstone fields will be stamped by a supervised reconciliation.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {missing.length > 0 && (
        <div
          data-testid="invariant-alert-missing-address"
          className="rounded-[14px] border border-amber-300 bg-amber-50 p-5"
        >
          <div className="flex items-start gap-3">
            <MapPin size={22} weight="duotone" className="text-amber-800 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="text-amber-900 font-display font-semibold text-base">
                Shipping address incomplete — Shippo label buy blocked
              </div>
              <div className="mt-2 text-sm text-amber-900">
                The following required field(s) are missing on this order:
                <span
                  className="ml-1 font-mono"
                  data-testid="invariant-missing-fields"
                >
                  {missing.join(", ")}
                </span>
              </div>
              <div className="mt-2 text-[12px] text-amber-900">
                Preview a reconciliation below to see what Stripe currently
                reports for this session — no mutations, no charges.
              </div>
            </div>
          </div>
        </div>
      )}

      <div
        data-testid="invariant-preview-panel"
        className="rounded-[14px] border border-brand-border bg-white p-5"
      >
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <MagnifyingGlass size={20} weight="duotone" className="text-brand-accent shrink-0 mt-0.5" />
            <div className="min-w-0">
              <div className="font-display font-semibold text-brand-navy text-base">
                Preview reconciliation (dry-run only)
              </div>
              <div className="mt-1 text-[12px] text-brand-muted max-w-2xl">
                Runs a read-only lookup against Stripe + the fulfillment
                pipeline. Never creates a Stripe session, PaymentIntent,
                charge, refund, or mutation on the order row. Wet-run
                recovery is intentionally not available from this UI.
              </div>
            </div>
          </div>
          <button
            onClick={onPreviewReconcile}
            disabled={dryRunBusy || !order.session_id}
            data-testid="invariant-preview-btn"
            className="h-10 px-4 rounded-[12px] border border-brand-navy bg-brand-navy text-white text-xs font-semibold hover:bg-brand-navy2 disabled:opacity-40 transition-colors"
          >
            {dryRunBusy ? "Previewing…" : "Preview reconcile (dry-run)"}
          </button>
        </div>

        {dryRunPlan && (
          <DryRunPlanView plan={dryRunPlan} />
        )}
      </div>
    </div>
  );
}

function KV({ label, value, testid, mono = true }) {
  return (
    <div className="border border-brand-border rounded-[10px] px-3 py-2 bg-brand-surface/60">
      <div className="text-[10px] font-mono uppercase tracking-widest text-brand-muted">
        {label}
      </div>
      <div
        data-testid={testid}
        className={mono ? "font-mono text-xs text-brand-navy break-all" : "text-sm text-brand-navy"}
      >
        {value ?? "—"}
      </div>
    </div>
  );
}

function DryRunPlanView({ plan }) {
  const invariants = plan?.invariants_blocking_wet_run || [];
  return (
    <div className="mt-5 space-y-4" data-testid="dry-run-plan">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KV label="Action" value={plan.action} testid="dry-run-action" mono={false} />
        <KV
          label="Stripe payment"
          value={plan.stripe_payment_status || plan.stripe_payment_intent_status || "—"}
          testid="dry-run-stripe-status"
        />
        <KV
          label="Stripe charges"
          value={plan.stripe_charge_count ?? "—"}
          testid="dry-run-charge-count"
        />
        <KV
          label="Order rows"
          value={plan.order_count ?? "—"}
          testid="dry-run-order-count"
        />
      </div>

      {plan.duplicate_conflict && (
        <div
          data-testid="dry-run-duplicate-block"
          className="rounded-[12px] border border-red-200 bg-red-50 p-4 text-xs"
        >
          <div className="flex items-center gap-2 text-red-900 font-semibold uppercase tracking-widest">
            <Copy size={13} weight="bold" /> Duplicate conflict
          </div>
          <div className="mt-2 space-y-1 font-mono text-red-900">
            <div>canonical: <span data-testid="dry-run-canonical-id">{plan.canonical_order_id || "—"}</span></div>
            <div>
              duplicates:{" "}
              <span data-testid="dry-run-duplicate-ids">
                {(plan.duplicate_order_ids || []).join(", ") || "none"}
              </span>
            </div>
          </div>
        </div>
      )}

      {plan.address_refresh_plan && (
        <div
          data-testid="dry-run-address-refresh"
          className="rounded-[12px] border border-amber-200 bg-amber-50 p-4 text-xs"
        >
          <div className="flex items-center gap-2 text-amber-900 font-semibold uppercase tracking-widest">
            <MapPin size={13} weight="bold" /> Address refresh plan
          </div>
          <div className="mt-2 space-y-1 text-amber-900">
            <div>
              current missing:{" "}
              <span className="font-mono">
                {(plan.address_refresh_plan.current_missing_fields || []).join(", ") || "none"}
              </span>
            </div>
            <div>
              stripe reports complete:{" "}
              <span className="font-mono">
                {String(plan.address_refresh_plan.stripe_reports_complete)}
              </span>
            </div>
            <div>
              stripe known fields:{" "}
              <span className="font-mono">
                {(plan.address_refresh_plan.stripe_known_fields || []).join(", ") || "none"}
              </span>
            </div>
          </div>
        </div>
      )}

      {invariants.length > 0 && (
        <div
          data-testid="dry-run-invariants-blocking"
          className="rounded-[12px] border border-red-200 bg-red-50 p-4 text-xs"
        >
          <div className="flex items-center gap-2 text-red-900 font-semibold uppercase tracking-widest">
            <ShieldWarning size={13} weight="bold" /> Blocked invariants
          </div>
          <div className="mt-2 font-mono text-red-900">
            {invariants.join(", ")}
          </div>
          <div className="mt-2 text-red-800 text-[11px]">
            An operator must resolve these before any wet-run recovery can proceed.
          </div>
        </div>
      )}

      {plan.stripe_lookup_error && (
        <div
          data-testid="dry-run-stripe-error"
          className="rounded-[12px] border border-red-200 bg-red-50 p-4 text-xs text-red-900 font-mono break-all"
        >
          Stripe lookup error: {plan.stripe_lookup_error}
        </div>
      )}

      <details className="mt-1">
        <summary className="cursor-pointer text-[11px] font-mono text-brand-muted hover:text-brand-navy">
          Raw plan JSON (read-only)
        </summary>
        <pre
          data-testid="dry-run-raw-json"
          className="mt-2 max-h-80 overflow-auto rounded-[10px] border border-brand-border bg-brand-surface/60 p-3 text-[11px] font-mono text-brand-text"
        >
          {JSON.stringify(plan, null, 2)}
        </pre>
      </details>
    </div>
  );
}
